import React, { useState, useEffect } from 'react';
import './WinReport.css';

const GTM_API_URL = 'https://newton-gtm.vercel.app';

const emptyContact = () => ({ name: '', role: '', phone: '', location: '' });

const WinReport = ({ quoteId }) => {
  const [loading, setLoading] = useState(true);
  const [quote, setQuote] = useState(null);
  const [fetchError, setFetchError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [contacts, setContacts] = useState([emptyContact()]);
  const [decisionMaker, setDecisionMaker] = useState('');
  const [locationDetails, setLocationDetails] = useState('');
  const [itContact, setItContact] = useState('');
  const [whyTheyBought, setWhyTheyBought] = useState('');
  const [painPoint1, setPainPoint1] = useState('');
  const [painPoint2, setPainPoint2] = useState('');
  const [painPoint3, setPainPoint3] = useState('');
  const [competitor, setCompetitor] = useState('');
  const [featuresWon, setFeaturesWon] = useState('');
  const [redFlags, setRedFlags] = useState('');
  const [anythingElse, setAnythingElse] = useState('');

  useEffect(() => {
    if (!quoteId) {
      setFetchError('No quote ID provided');
      setLoading(false);
      return;
    }

    const fetchQuote = async () => {
      try {
        const res = await fetch(`${GTM_API_URL}/api/v2/quotes/${quoteId}`);
        const data = await res.json();
        if (data.success && data.quote) {
          setQuote(data.quote);
        } else {
          setFetchError(data.error || 'Quote not found');
        }
      } catch (err) {
        setFetchError('Failed to load quote details');
      } finally {
        setLoading(false);
      }
    };

    fetchQuote();
  }, [quoteId]);

  const updateContact = (index, field, value) => {
    setContacts(prev => prev.map((c, i) => i === index ? { ...c, [field]: value } : c));
  };

  const addContact = () => {
    setContacts(prev => [...prev, emptyContact()]);
  };

  const removeContact = (index) => {
    if (contacts.length <= 1) return;
    setContacts(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      const res = await fetch(`${GTM_API_URL}/api/v2/quotes/${quoteId}/win-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contacts,
          decisionMaker,
          locationDetails,
          itContact,
          whyTheyBought,
          painPoint1,
          painPoint2,
          painPoint3,
          competitor,
          featuresWon,
          redFlags,
          anythingElse,
        }),
      });

      const data = await res.json();

      if (data.success) {
        setSuccess(data.message || 'Win report submitted successfully!');
      } else {
        setError(data.error || 'Failed to submit win report');
      }
    } catch (err) {
      setError(err.message || 'Error submitting win report');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="win-report-loading">Loading quote details...</div>;
  }

  if (fetchError) {
    return (
      <div className="win-report-error-page">
        <h2>Error</h2>
        <p>{fetchError}</p>
      </div>
    );
  }

  if (success) {
    return (
      <div className="win-report-container">
        <div className="win-report-alert win-report-alert-success">
          {success}
        </div>
        <p style={{ textAlign: 'center', color: '#666' }}>You can close this page.</p>
      </div>
    );
  }

  return (
    <div className="win-report-container">
      <div className="win-report-header">
        <h1>Win Report</h1>
        <p>Fill out the details below so our onboarding team has the context they need.</p>
        {quote?.organizationName && (
          <div className="win-report-org-badge">{quote.organizationName}</div>
        )}
      </div>

      {error && <div className="win-report-alert win-report-alert-error">{error}</div>}

      <form onSubmit={handleSubmit}>
        {/* People / Contacts Section */}
        <div className="win-report-section">
          <h2>People / Contacts</h2>

          {contacts.map((contact, idx) => (
            <div className="win-report-contact-card" key={idx}>
              <h3>
                Contact {idx + 1}
                {contacts.length > 1 && (
                  <button type="button" className="win-report-remove-btn" onClick={() => removeContact(idx)}>
                    Remove
                  </button>
                )}
              </h3>
              <div className="win-report-contact-row">
                <div className="win-report-contact-field">
                  <label>Name</label>
                  <input type="text" value={contact.name} onChange={e => updateContact(idx, 'name', e.target.value)} placeholder="Jane Smith" />
                </div>
                <div className="win-report-contact-field">
                  <label>Role</label>
                  <input type="text" value={contact.role} onChange={e => updateContact(idx, 'role', e.target.value)} placeholder="Office Manager" />
                </div>
              </div>
              <div className="win-report-contact-row">
                <div className="win-report-contact-field">
                  <label>Cell / Direct Work Number</label>
                  <input type="text" value={contact.phone} onChange={e => updateContact(idx, 'phone', e.target.value)} placeholder="(555) 123-4567" />
                </div>
                <div className="win-report-contact-field">
                  <label>Location They Work Out Of</label>
                  <input type="text" value={contact.location} onChange={e => updateContact(idx, 'location', e.target.value)} placeholder="Main Office" />
                </div>
              </div>
            </div>
          ))}

          <button type="button" className="win-report-add-btn" onClick={addContact}>
            + Add Another Contact
          </button>
        </div>

        {/* Organization Details Section */}
        <div className="win-report-section">
          <h2>Organization Details</h2>

          <div className="win-report-form-group">
            <label>Decision Maker</label>
            <input type="text" value={decisionMaker} onChange={e => setDecisionMaker(e.target.value)} placeholder="Name and title of the decision maker" />
          </div>

          <div className="win-report-form-group">
            <label>Address, Main Phone Number, POC at Each Location</label>
            <textarea value={locationDetails} onChange={e => setLocationDetails(e.target.value)} placeholder="List each location with its address, main phone number, and point of contact" />
          </div>

          <div className="win-report-form-group">
            <label>IT Contact Info</label>
            <input type="text" value={itContact} onChange={e => setItContact(e.target.value)} placeholder="Name, email, phone number" />
          </div>
        </div>

        {/* Deal Intel Section */}
        <div className="win-report-section">
          <h2>Deal Intel</h2>

          <div className="win-report-form-group">
            <label>Why They Bought</label>
            <textarea value={whyTheyBought} onChange={e => setWhyTheyBought(e.target.value)} placeholder="What drove them to choose Newton?" />
          </div>

          <div className="win-report-form-group">
            <label>Key Pain Points (Rank Top 3 by Severity)</label>
            <div className="win-report-pain-points">
              <div className="win-report-pain-row">
                <div className="win-report-pain-rank">1</div>
                <input type="text" value={painPoint1} onChange={e => setPainPoint1(e.target.value)} placeholder="Most severe pain point" />
              </div>
              <div className="win-report-pain-row">
                <div className="win-report-pain-rank">2</div>
                <input type="text" value={painPoint2} onChange={e => setPainPoint2(e.target.value)} placeholder="Second pain point" />
              </div>
              <div className="win-report-pain-row">
                <div className="win-report-pain-rank">3</div>
                <input type="text" value={painPoint3} onChange={e => setPainPoint3(e.target.value)} placeholder="Third pain point" />
              </div>
            </div>
          </div>

          <div className="win-report-form-group">
            <label>Competitor</label>
            <input type="text" value={competitor} onChange={e => setCompetitor(e.target.value)} placeholder="Who were they comparing us against?" />
          </div>

          <div className="win-report-form-group">
            <label>Features That Won the Deal</label>
            <textarea value={featuresWon} onChange={e => setFeaturesWon(e.target.value)} placeholder="Which features or capabilities sealed the deal?" />
          </div>

          <div className="win-report-form-group">
            <label>Any Red Flags / Concerns</label>
            <textarea value={redFlags} onChange={e => setRedFlags(e.target.value)} placeholder="Anything the onboarding team should watch out for?" />
          </div>

          <div className="win-report-form-group">
            <label>Anything Else We Need to Know About the Client</label>
            <textarea value={anythingElse} onChange={e => setAnythingElse(e.target.value)} placeholder="Any other context that would help the team" />
          </div>
        </div>

        <button type="submit" className="win-report-submit-btn" disabled={submitting}>
          {submitting ? 'Submitting...' : 'Submit Win Report'}
        </button>
      </form>
    </div>
  );
};

export default WinReport;
